import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import moment from 'moment';
import { Field, Formik, FormikErrors } from 'formik';
import { DataCard, Loader, SelectOption } from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  EndorsementServiceDomainGender,
  UpsertApplicationGroupRequest,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { FormInput, FormSelect } from '@sdir/blueprint.aps/lib/components';
import FormDate from '@sdir/blueprint.aps/lib/components/Molecules/FormDate';
import { sortListByAttribute } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import { routes } from '../Routes';
import { endorsementApi } from '../../httpclient';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import { useNationalities } from '../../services/hooks/useNationalities';
import { getPrevStep } from '../../helpers/Steps';
import { useReportees } from '../../services/hooks/useReportees';
import { isEmailInvalid } from '../../helpers/validationHelpers';

type SailorInput = {
  name?: string | null;
  dateOfBirth?: string | null;
  email?: string;
  citizenship?: string | null;
  dNumber?: string | null;
  gender?: EndorsementServiceDomainGender | null;
};

const maxDobDate = moment().subtract(18, 'y');

const SailorPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: { result: reportees },
  } = useReportees();
  const { application: currentApplication, setApplication } = useCurrentApplication();
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const emailText = intl.formatMessage({ id: 'general.form.error.email' });

  const {
    result: nationalities,
    error: nationalitiesError,
    loading: nationalitiesLoading,
  } = useNationalities();

  const {
    errorObject: submitError,
    loading: submitLoading,
    callApi: submitStep,
  } = useApi({
    apiFunction: (payload) => endorsementApi.upsertApplicationGroup(payload),
    callApiOnInit: false,
    successCallback: (result) => {
      if (result && setApplication) {
        setApplication({ result });
      }
      const path = generatePath(routes.certificate, { applicationUid: result.uid });
      navigate(path);
    },
  });

  const onNavBack = () => {
    const prev = getPrevStep(location.pathname, !!reportees?.length);
    if (prev?.route) {
      const path = generatePath(prev?.route, { applicationUid: currentApplication?.result?.uid });
      navigate(path);
    } else {
      navigate(-1);
    }
  };

  const citizenOptions = useMemo(() => {
    if (!nationalities) return [];
    const items = nationalities.map<SelectOption>((v) => ({
      name: v?.[intl?.locale] || '',
      value: v.idIso2 || '',
    }));
    return sortListByAttribute(items, 'name');
  }, [nationalities, intl?.locale]);

  const genderOptions = useMemo(() => {
    return [
      { name: EndorsementServiceDomainGender.Female, value: EndorsementServiceDomainGender.Female },
      { name: EndorsementServiceDomainGender.Male, value: EndorsementServiceDomainGender.Male },
    ];
  }, []);

  const handleSubmit = useCallback(
    async (values: SailorInput) => {
      const existingApplication = currentApplication?.result || {};
      const dob = values?.dateOfBirth
        ? moment(values.dateOfBirth).format(moment.HTML5_FMT.DATE)
        : values?.dateOfBirth;
      const payload: UpsertApplicationGroupRequest | any = {
        ...existingApplication,
        person: {
          name: values.name,
          dateOfBirth: dob,
          nationality: values?.citizenship,
          email: values.email,
          id: values.dNumber,
          gender: values.gender,
        },
      };
      submitStep(payload);
    },
    [currentApplication?.result]
  );

  const handleValidation = useCallback(
    (values: SailorInput) => {
      const errors: FormikErrors<any> = {};
      if (!values.name) {
        errors.name = requiredText;
      } else if ((values.name?.trim() || '').split(' ')?.length < 2) {
        errors.name = intl.formatMessage({ id: 'general.form.error.mandatorylastname' });
      }
      if (!values.dateOfBirth) errors.dateOfBirth = requiredText;
      if (!values.email) {
        errors.email = requiredText;
      } else if (isEmailInvalid(values.email)) {
        errors.email = emailText;
      }
      if (!values.citizenship) errors.citizenship = requiredText;
      if (!values.gender) errors.gender = requiredText;
      if (values.dNumber) {
        const isNumber = /^\d+$/.test(values.dNumber);
        if (!isNumber) {
          errors.dNumber = intl.formatMessage({ id: 'general.form.error.dNumber' });
        }
      }
      return Object.keys(errors).length ? errors : undefined;
    },
    [requiredText, emailText]
  );

  const initialValues: SailorInput = useMemo(() => {
    const application = currentApplication?.result;
    const person = application?.person;
    return {
      name: person?.name || '',
      dateOfBirth: person?.dateOfBirth || null,
      email: person?.email || '',
      citizenship: person?.nationality || null,
      dNumber: person?.id || '',
      gender: person?.gender || null,
    };
  }, [currentApplication?.result]);

  if (nationalitiesLoading || currentApplication?.loading) {
    return <Loader />;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={handleValidation}>
      {({ submitForm }) => (
        <form noValidate>
          <ApplicationLayout
            canNavNext={!submitLoading}
            canNavBack={!submitLoading}
            heading={intl.formatMessage({ id: 'application.new.title' })}
            ingress={intl.formatMessage({ id: 'application.new.ingress' })}
            exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
            navBackLabel={intl.formatMessage({ id: 'general.back' })}
            navNextLabel={intl.formatMessage({ id: 'general.next' })}
            timeline={<ApplicationSteps />}
            onNavNextClick={submitForm}
            onNavBackClick={onNavBack}
          >
            <DataCard>
              <SailorInfo>
                <ManualRegistration>
                  <FormContent>
                    <h2>{intl.formatMessage({ id: 'application.sailor.title' })}</h2>
                    <FieldRow>
                      <Field
                        name="name"
                        label={intl.formatMessage({ id: 'general.form.name' })}
                        component={FormInput}
                      />
                      <Field
                        name="gender"
                        label={intl.formatMessage({ id: 'general.form.gender' })}
                        component={FormSelect}
                        options={genderOptions}
                      />
                    </FieldRow>
                    <FieldRow>
                      <Field
                        name="dateOfBirth"
                        label={intl.formatMessage({ id: 'general.form.dateOfBirth' })}
                        component={FormDate}
                        locale={intl?.locale || 'en'}
                        format="dd.MM.yyyy"
                        maxDate={maxDobDate}
                        showMonthDropdown
                        showYearDropdown
                      />
                      <Field
                        name="citizenship"
                        label={intl.formatMessage({
                          id: 'application.sailor.citizenship',
                        })}
                        component={FormSelect}
                        options={citizenOptions}
                        searchable
                      />
                    </FieldRow>
                    <FieldRow>
                      <Field
                        name="email"
                        label={intl.formatMessage({ id: 'application.sailor.email' })}
                        component={FormInput}
                      />
                      <Field
                        name="dNumber"
                        label={intl.formatMessage({ id: 'application.sailor.dNumber' })}
                        component={FormInput}
                      />
                    </FieldRow>
                    <p>{intl.formatMessage({ id: 'application.sailor.emailinfo' })}</p>
                  </FormContent>
                </ManualRegistration>
              </SailorInfo>
            </DataCard>
            {submitError && (
              <ErrorWrapper>
                <ErrorMessage error={submitError} />
              </ErrorWrapper>
            )}
          </ApplicationLayout>
        </form>
      )}
    </Formik>
  );
};

export default SailorPage;

const SailorInfo = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;
`;

const ManualRegistration = styled.div``;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;

  & > * {
    width: 300px;

    &:not(last-child) {
      margin-right: 30px;
    }
  }

  &.fullwidth {
    & > * {
      width: calc(300px * 2 + 30px);
    }
  }
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ErrorWrapper = styled.div`
  margin-bottom: 30px;
`;
